import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Tab,
  Tabs,
  Paper,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  Stack,
  LinearProgress,
  Badge,
  TablePagination,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  TextField,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TableSortLabel,
} from '@mui/material';
import {
  LocationOn,
  Route,
  Timeline,
  Assessment,
  PictureAsPdf,
  AccessTime,
  Place,
  Refresh,
  TrendingUp,
  Navigation,
  Schedule,
  Person,
  CalendarToday,
  MyLocation,
  Search,
  Clear,
  GridView,
  Warning as WarningIcon,
  Security as SecurityIcon,
  ExpandMore as ExpandMoreIcon,
  Lightbulb as LightbulbIcon,
  BarChart as BarChartIcon,
} from '@mui/icons-material';


import { useReactToPrint } from 'react-to-print';

import { AnalysisJob, DwellLocation, Route as RouteType } from '@signals/app-shell/types';
import apiService from '@signals/app-shell/services/api';
import { formatDateRange } from '@signals/app-shell/utils/dateUtils';
import EnhancedMapVisualizationGL from './EnhancedMapVisualizationGL';
import DeviceBreakdownCard from './DeviceBreakdownCard';
import DwellHeatmapVisx from './DwellHeatmapVisx';
import DwellGanttChart from './DwellGanttChart';
import { useParticipantLabels } from '@signals/app-shell/hooks/useParticipantLabels';
import RouteHeatmapVisx from './RouteHeatmapVisx';
import RouteGanttChart from './RouteGanttChart';
import PolReportViewer from './PolReportViewer';




interface EnhancedResultsViewerProps {
  jobId: number;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`results-tabpanel-${index}`}
      aria-labelledby={`results-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const EnhancedResultsViewer: React.FC<EnhancedResultsViewerProps> = ({ jobId }) => {
  const { singular, plural, pluralLower } = useParticipantLabels();
  const [job, setJob] = useState<AnalysisJob | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [mapCenter, setMapCenter] = useState<[number, number] | null>(null);
  const [dwellPage, setDwellPage] = useState(0);
  const [routePage, setRoutePage] = useState(0);
  const [selectedDeviceTypes, setSelectedDeviceTypes] = useState<string[]>(['all']);
  const [dwellSearchTerm, setDwellSearchTerm] = useState('');
  const [routeSearchTerm, setRouteSearchTerm] = useState('');
  const [dwellRowsPerPage, setDwellRowsPerPage] = useState(20);
  const [routeRowsPerPage, setRouteRowsPerPage] = useState(20);
  const [temporalVisualization, setTemporalVisualization] = useState<'heatmap' | 'timeline'>('heatmap');
  const [routeTemporalVisualization, setRouteTemporalVisualization] = useState<'heatmap' | 'timeline'>('heatmap');
  const [sharedMapExpanded, setSharedMapExpanded] = useState(true);
  
  // Selection state for table-map filtering
  const [selectedDwellId, setSelectedDwellId] = useState<number | null>(null);
  const [selectedRouteId, setSelectedRouteId] = useState<number | null>(null);
  
  // Sorting state for tables
  const [dwellSortBy, setDwellSortBy] = useState<string>('total_visits');
  const [dwellSortOrder, setDwellSortOrder] = useState<'asc' | 'desc'>('desc');
  const [routeSortBy, setRouteSortBy] = useState<string>('frequency');
  const [routeSortOrder, setRouteSortOrder] = useState<'asc' | 'desc'>('desc');
  const printRef = useRef<HTMLDivElement>(null);

  // Sorting handlers
  const handleDwellSort = (column: string) => {
    if (dwellSortBy === column) {
      setDwellSortOrder(dwellSortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setDwellSortBy(column);
      setDwellSortOrder('desc'); // Default to descending for new columns
    }
  };

  const handleRouteSort = (column: string) => {
    if (routeSortBy === column) {
      setRouteSortOrder(routeSortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setRouteSortBy(column);
      setRouteSortOrder('desc'); // Default to descending for new columns
    }
  };

  // Sorting functions
  const sortDwellLocations = (locations: DwellLocation[]) => {
    return [...locations].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (dwellSortBy) {
        case 'place_name':
          aValue = (a.place_name || '').toLowerCase();
          bValue = (b.place_name || '').toLowerCase();
          break;
        case 'place_type':
          aValue = (a.place_type || '').toLowerCase();
          bValue = (b.place_type || '').toLowerCase();
          break;
        case 'total_visits':
          aValue = a.total_visits || 0;
          bValue = b.total_visits || 0;
          break;
        case 'average_duration':
          aValue = a.average_duration || 0;
          bValue = b.average_duration || 0;
          break;
        case 'total_duration':
          aValue = a.total_duration || 0;
          bValue = b.total_duration || 0;
          break;
        case 'device_type':
          aValue = (a.device_type || '').toLowerCase();
          bValue = (b.device_type || '').toLowerCase();
          break;
        default:
          return 0;
      }

      if (typeof aValue === 'string') {
        return dwellSortOrder === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      } else {
        return dwellSortOrder === 'asc' 
          ? aValue - bValue
          : bValue - aValue;
      }
    });
  };

  const sortRoutes = (routesData: RouteType[]) => {
    return [...routesData].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (routeSortBy) {
        case 'route_name':
          aValue = (a.route_name || '').toLowerCase();
          bValue = (b.route_name || '').toLowerCase();
          break;
        case 'frequency':
          aValue = a.frequency || 0;
          bValue = b.frequency || 0;
          break;
        case 'distance':
          aValue = a.distance || 0;
          bValue = b.distance || 0;
          break;
        case 'average_duration':
          aValue = a.average_duration || 0;
          bValue = b.average_duration || 0;
          break;
        case 'device_type':
          aValue = (a.device_type || '').toLowerCase();
          bValue = (b.device_type || '').toLowerCase();
          break;
        case 'students':
          aValue = (a.students?.[0] || '').toLowerCase();
          bValue = (b.students?.[0] || '').toLowerCase();
          break;
        default:
          return 0;
      }

      if (typeof aValue === 'string') {
        return routeSortOrder === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      } else {
        return routeSortOrder === 'asc' 
          ? aValue - bValue
          : bValue - aValue;
      }
    });
  };

  // Paginated data state
  const [dwellData, setDwellData] = useState<{
    items: DwellLocation[];
    total: number;
    pages: number;
    loading: boolean;
  }>({ items: [], total: 0, pages: 0, loading: false });

  const [routeData, setRouteData] = useState<{
    items: RouteType[];
    total: number;
    pages: number;
    loading: boolean;
  }>({ items: [], total: 0, pages: 0, loading: false });

  // Move all filtering logic to before early returns
  const rawSummaryStats = job?.results_summary || {};
  
  // Multi-device detection
  const isMultiDevice = job?.analysis_type === 'multi_device';
  
  // Calculate current device filter for visualization components
  const currentDeviceFilter = selectedDeviceTypes.includes('all') ? 'all' : selectedDeviceTypes[0] || 'all';
  
  // Use paginated data for tables, but complete data for map visualization
  const combinedDwellLocations = sortDwellLocations(dwellData.items);
  const combinedRoutes = sortRoutes(routeData.items);
  
  // For map visualization, use complete dataset from job results
  const mapDwellLocations = job?.dwell_locations || [];
  const mapRoutes = job?.routes || [];


  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Analysis_Report_Job_${jobId}`,
  });

  const handleHtmlReportOpen = () => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      alert('Please sign in to open the professional report.');
      return;
    }

    const reportUrl = apiService.getHtmlReportUrl(jobId, { regenerate: true });
    const reportWindow = window.open(reportUrl, '_blank');
    if (!reportWindow) {
      alert('Please allow pop-ups for this site to open the professional report.');
    }
  };

  useEffect(() => {
    loadJobData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const loadJobData = async () => {
    try {
      setLoading(true);
      
      // First get the job details
      const jobData = await apiService.getAnalysisJob(jobId);
      
      // If the job is completed, get summary data only (not full results)
      if (jobData.status === 'completed') {
        try {
          const resultsData = await apiService.getAnalysisResults(jobId);
          
          // Merge summary data and complete datasets for map visualization
          const enrichedJobData = {
            ...jobData,
            results_summary: resultsData.summary_stats || jobData.results_summary,
            // Include complete datasets for map visualization (tables will use paginated data)
            dwell_locations: resultsData.dwell_locations || [],
            routes: resultsData.routes || []
          };
          
          setJob(enrichedJobData);
          
        } catch (resultsErr) {
          console.warn('Could not load detailed results, using job data only:', resultsErr);
          setJob({
            ...jobData,
            dwell_locations: jobData.dwell_locations || [],
            routes: jobData.routes || []
          });
        }
      } else {
        setJob(jobData);
      }
      
    } catch (err: any) {
      console.error('Error loading job data:', err);
      setError(err.message || 'Failed to load analysis results');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Load paginated dwell locations
  const loadDwellLocations = async () => {
    if (!job || job.status !== 'completed') return;

    try {
      setDwellData(prev => ({ ...prev, loading: true }));
      
      const currentDeviceFilter = selectedDeviceTypes.includes('all') ? undefined : selectedDeviceTypes[0];
      const response = await apiService.getDwellLocations(
        jobId,
        dwellPage,
        dwellRowsPerPage,
        dwellSearchTerm || undefined,
        currentDeviceFilter
      );
      
      setDwellData({
        items: response.items,
        total: response.total,
        pages: response.pages,
        loading: false
      });

      // Set map center based on first dwell location if available
      if (response.items.length > 0 && !mapCenter) {
        const firstDwell = response.items[0];
        if (firstDwell.center_latitude && firstDwell.center_longitude) {
          setMapCenter([firstDwell.center_latitude, firstDwell.center_longitude]);
        }
      }

    } catch (err: any) {
      console.error('Error loading dwell locations:', err);
      setDwellData(prev => ({ ...prev, loading: false }));
    }
  };

  // Load paginated routes
  const loadRoutes = async () => {
    if (!job || job.status !== 'completed') return;

    try {
      setRouteData(prev => ({ ...prev, loading: true }));
      
      const currentDeviceFilter = selectedDeviceTypes.includes('all') ? undefined : selectedDeviceTypes[0];
      const response = await apiService.getRoutes(
        jobId,
        routePage,
        routeRowsPerPage,
        routeSearchTerm || undefined,
        currentDeviceFilter
      );
      
      setRouteData({
        items: response.items,
        total: response.total,
        pages: response.pages,
        loading: false
      });

    } catch (err: any) {
      console.error('Error loading routes:', err);
      setRouteData(prev => ({ ...prev, loading: false }));
    }
  };

  // Load paginated data when dependencies change
  useEffect(() => {
    loadDwellLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job, dwellPage, dwellRowsPerPage, dwellSearchTerm, selectedDeviceTypes]);

  useEffect(() => {
    loadRoutes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job, routePage, routeRowsPerPage, routeSearchTerm, selectedDeviceTypes]);

  const formatDuration = (seconds: number): string => {
    if (!seconds) return '0m';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const formatDistance = (meters: number): string => {
    if (!meters) return '0 ft';
    const miles = meters * 0.000621371;
    if (miles < 0.1) {
      const feet = meters * 3.28084;
      return `${Math.round(feet)} ft`;
    }
    return `${miles.toFixed(2)} mi`;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
        <Button onClick={loadJobData} startIcon={<Refresh />} sx={{ ml: 2 }}>
          Retry
        </Button>
      </Alert>
    );
  }

  if (!job) {
    return <Alert severity="info">No job data available</Alert>;
  }

  // Pattern of Life report jobs use a dedicated viewer — the GPS dwell/route
  // tabs below don't apply to precomputed POL data.
  if (job.analysis_type === 'pol_report') {
    return (
      <Box sx={{ width: '100%', maxWidth: 'none', p: { xs: 2, sm: 3 } }}>
        <Paper elevation={3} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography
                variant="h4"
                component="h1"
                gutterBottom
                sx={{ fontWeight: 600, color: 'primary.main' }}
              >
                Established Pattern of Life
              </Typography>
              <Typography variant="h6" component="h2" color="text.secondary" gutterBottom>
                Job #{jobId} - {job.name || 'Unnamed Report'}
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                <Chip
                  icon={<Person />}
                  label={`${plural}: ${job.students?.join(', ') || 'All'}`}
                  variant="outlined"
                />
                <Chip
                  icon={<CalendarToday />}
                  label={formatDateRange(job.date_range_start, job.date_range_end)}
                  variant="outlined"
                />
              </Stack>
            </Grid>
            <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
              <Chip
                label={job.status.toUpperCase()}
                color={job.status === 'completed' ? 'success' : 'default'}
              />
            </Grid>
          </Grid>
        </Paper>
        <PolReportViewer job={job} />
      </Box>
    );
  }

  // Data and filtering logic already handled before early returns
  
  // Use normal summary stats and database relationships (same for both single and multi-device)
  const summaryStats: any = {
    ...rawSummaryStats,
    total_dwell_locations: dwellData.total || rawSummaryStats?.total_dwell_locations || 0,
    total_routes: routeData.total || rawSummaryStats?.total_routes || 0,
  };
  
  if (isMultiDevice) {
    // Use device_breakdown from backend - required for multi-device analysis
    if (rawSummaryStats?.device_breakdown) {
      summaryStats.device_breakdown = rawSummaryStats.device_breakdown;
    }
  }

  const heatmapData = summaryStats.heatmap_data || [];
  const daytimeHeatmapData = summaryStats.daytime_heatmap_data || [];
  const nighttimeHeatmapData = summaryStats.nighttime_heatmap_data || [];
  const deviceTracks = summaryStats.device_tracks || {};
  const temporalBreakdown = summaryStats.temporal_breakdown;
  
  // Use visualization data directly - device filtering is handled server-side for tables
  // Map visualization will show all data as it loads from summary data
  const filteredHeatmapData = heatmapData;
  const filteredDeviceTracks = deviceTracks;
  const filteredDaytimeData = daytimeHeatmapData;
  const filteredNighttimeData = nighttimeHeatmapData;

  // Calculate additional statistics using combined data
  const totalDwellTime = combinedDwellLocations.reduce((sum, dwell) => sum + (dwell.total_duration || 0), 0);
  const averageDwellTime = combinedDwellLocations.length > 0 ? totalDwellTime / combinedDwellLocations.length : 0;
  
  // 🔧 DEFENSIVE: Safe mostVisitedLocation calculation
  let mostVisitedLocation: DwellLocation | null = null;
  if (combinedDwellLocations.length > 0) {
    try {
      mostVisitedLocation = combinedDwellLocations.reduce((max, dwell) => {
        const currentVisits = dwell?.total_visits || 0;
        const maxVisits = max?.total_visits || 0;
        return currentVisits > maxVisits ? dwell : max;
      }, combinedDwellLocations[0]);
    } catch (error) {
      console.warn('Error calculating mostVisitedLocation:', error);
      mostVisitedLocation = combinedDwellLocations[0] || null;
    }
  }

  // SAFETY CHECK: Ensure summaryStats structure is valid
  if (summaryStats && typeof summaryStats !== 'object') {
    console.warn('SAFETY CHECK: summaryStats has unexpected type:', typeof summaryStats);
  }

  return (
    <Box sx={{ 
      width: '100%', 
      maxWidth: 'none', 
      p: { xs: 2, sm: 3 }
    }} id="main-container">

      
      <div ref={printRef} style={{ width: '100%' }}>
        {/* Header Section */}
        <Paper elevation={3} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                GIS Analysis Report
              </Typography>
              <Typography variant="h6" component="h2" color="text.secondary" gutterBottom>
                Job #{jobId} - {job.name || 'Unnamed Analysis'}
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                <Chip 
                  icon={<Schedule />}
                  label={`Generated: ${job.end_time ? new Date(job.end_time).toLocaleString() : 'In Progress'}`}
                  variant="outlined"
                />
                <Chip 
                  icon={<Person />}
                  label={`${plural}: ${job.students?.join(', ') || 'All'}`}
                  variant="outlined"
                  sx={{
                    height: 'auto',
                    maxWidth: '100%',
                    '& .MuiChip-label': {
                      whiteSpace: 'normal',
                      display: 'block',
                      py: 0.5,
                    },
                  }}
                />
                <Chip 
                  icon={<CalendarToday />}
                  label={formatDateRange(job.date_range_start, job.date_range_end)}
                  variant="outlined"
                />
              </Stack>
            </Grid>
            <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
              <Stack direction="row" spacing={2} justifyContent={{ xs: 'flex-start', md: 'flex-end' }} sx={{ mb: 1 }}>
                <Chip 
                  label={job.status.toUpperCase()} 
                  color={job.status === 'completed' ? 'success' : 'default'}
                />
                <Tooltip title="Export Current Tab to PDF">
                  <IconButton onClick={handlePrint} color="primary" size="large">
                    <PictureAsPdf />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Professional PDF Report">
                  <IconButton 
                    onClick={handleHtmlReportOpen}
                    color="secondary" 
                    size="large"
                  >
                    <Assessment />
                  </IconButton>
                </Tooltip>
              </Stack>
              {job.processing_duration && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Processing Time: {formatDuration(job.processing_duration)}
                </Typography>
              )}
            </Grid>
          </Grid>

          {/* Device Type Filter - Only show for multi-device analysis */}
          {isMultiDevice && summaryStats.device_breakdown && Object.keys(summaryStats.device_breakdown).length > 0 && (
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              <Grid container spacing={3} alignItems="center">
                <Grid item>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary', minWidth: 'max-content' }}>
                    Device Filter
                  </Typography>
                </Grid>
                <Grid item sx={{ minWidth: 200 }}>
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <Select
                      value={selectedDeviceTypes.includes('all') ? 'all' : selectedDeviceTypes[0] || 'all'}
                      onChange={(e: SelectChangeEvent<string>) => {
                        const value = e.target.value;
                        if (value === 'all') {
                          setSelectedDeviceTypes(['all']);
                        } else {
                          setSelectedDeviceTypes([value]);
                        }
                      }}
                      displayEmpty
                      variant="outlined"
                      sx={{
                        minWidth: 120,
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'divider'
                        }
                      }}
                    >
                      <MenuItem value="all">All Devices</MenuItem>
                      {Object.keys(summaryStats.device_breakdown).map(deviceType => (
                        <MenuItem key={deviceType} value={deviceType}>
                          {deviceType}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item>
                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    Filters all data below
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </Paper>



        {/* Key Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #2C5AA0 0%, #1e3a5f 100%)', color: 'white' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <LocationOn sx={{ fontSize: 48, mb: 1, opacity: 0.9 }} />
                <Typography variant="h3" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {summaryStats.total_dwell_locations || combinedDwellLocations.length}
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  Dwell Locations
                </Typography>
                <Typography variant="caption" display="block" sx={{ mt: 1, opacity: 0.7 }}>
                  Identified stop points
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #0891b2 0%, #0e7490 100%)', color: 'white' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Route sx={{ fontSize: 48, mb: 1, opacity: 0.9 }} />
                <Typography variant="h3" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {summaryStats.total_routes || combinedRoutes.length}
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  Dwell Route Patterns
                </Typography>
                <Typography variant="caption" display="block" sx={{ mt: 1, opacity: 0.7 }}>
                  Unique paths detected
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)', color: 'white' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Timeline sx={{ fontSize: 48, mb: 1, opacity: 0.9 }} />
                <Typography variant="h3" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {(() => {
                    // Try multiple field names for compatibility
                    const points = summaryStats.total_points || 
                                  summaryStats.total_gps_points || 
                                  summaryStats.temporal_breakdown?.total_points || 
                                  0;
                    return points.toLocaleString();
                  })()}
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  GPS Points
                </Typography>
                <Typography variant="caption" display="block" sx={{ mt: 1, opacity: 0.7 }}>
                  Data points analyzed
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #0d9488 0%, #115e59 100%)', color: 'white' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <AccessTime sx={{ fontSize: 48, mb: 1, opacity: 0.9 }} />
                <Typography variant="h3" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {formatDuration(totalDwellTime)}
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  Total Dwell Time
                </Typography>
                <Typography variant="caption" display="block" sx={{ mt: 1, opacity: 0.7 }}>
                  Time spent at locations
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Quick Insights */}
        <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <TrendingUp sx={{ mr: 1 }} />
            Key Insights
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <List dense>
                <ListItem>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      <MyLocation />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText 
                    primary="Most Visited Location"
                    secondary={mostVisitedLocation?.place_name || mostVisitedLocation?.address || 'Unknown Location'}
                  />
                  <Typography variant="h6" color="primary">
                    {mostVisitedLocation?.total_visits || 0} visits
                  </Typography>
                </ListItem>
                <Divider variant="inset" component="li" />
                <ListItem>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'secondary.main' }}>
                      <AccessTime />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText 
                    primary="Average Dwell Time"
                    secondary="Time spent per location"
                  />
                  <Typography variant="h6" color="secondary">
                    {formatDuration(
                      summaryStats.average_dwell_time || averageDwellTime
                    )}
                  </Typography>
                </ListItem>
              </List>
            </Grid>
            <Grid item xs={12} md={6}>
              <List dense>
                <ListItem>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'success.main' }}>
                      <Navigation />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText 
                    primary="Total Route Distance"
                    secondary="Combined distance of all routes"
                  />
                  <Typography variant="h6" color="success.main">
                    {formatDistance(
                      summaryStats.total_route_distance || 0
                    )}
                  </Typography>
                </ListItem>
                <Divider variant="inset" component="li" />
                <ListItem>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'warning.main' }}>
                      <Person />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText 
                    primary={`${plural} analyzed`}
                    secondary={`Number of unique ${pluralLower}`}
                  />
                  <Typography variant="h6" color="warning.main">
                    {job.students?.length || summaryStats.students_analyzed || 'N/A'}
                  </Typography>
                </ListItem>
              </List>
            </Grid>
          </Grid>
        </Paper>

        {/* Multi-Device Analysis Breakdown */}
        {(() => {
          const shouldShowDeviceBreakdown = summaryStats.device_breakdown && Object.keys(summaryStats.device_breakdown).length > 0;
          
          // Show device breakdown for multi-device analysis even if only one device has data
          if (shouldShowDeviceBreakdown && job?.analysis_type === 'multi_device') {
            return (
              <DeviceBreakdownCard 
                deviceBreakdown={summaryStats.device_breakdown}
                totalEntities={summaryStats.unique_devices || summaryStats.total_device_types || 1}
                isMultiDeviceComparison={true}
              />
            );
          }
          return null;
        })()}

        {/* Shared Interactive Map */}
        <Accordion 
          expanded={sharedMapExpanded} 
          onChange={(_, isExpanded) => setSharedMapExpanded(isExpanded)}
          sx={{ mb: 2 }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{ 
              bgcolor: 'action.hover',
              '& .MuiAccordionSummary-content': {
                alignItems: 'center'
              }
            }}
          >
            <Place sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6" sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
              Interactive Map
              {(selectedDwellId || selectedRouteId) && (
                <Chip 
                  label={selectedDwellId ? "Dwell Selected" : "Route Selected"}
                  size="small"
                  color="primary"
                  sx={{ ml: 1 }}
                  onDelete={() => {
                    setSelectedDwellId(null);
                    setSelectedRouteId(null);
                  }}
                />
              )}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
              {(() => {
                switch (tabValue) {
                  case 0: return 'Overview - All Layers';
                  case 1: return 'Dwell Locations Focus';
                  case 2: return 'Route Patterns Focus';
                  case 3: return 'Day vs Night Activity';
                  default: return 'Interactive Map';
                }
              })()}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0, minHeight: '600px' }}>
            <EnhancedMapVisualizationGL
              dwellLocations={mapDwellLocations}
              routes={mapRoutes}
              heatmapData={filteredHeatmapData}
              daytimeHeatmapData={filteredDaytimeData}
              nighttimeHeatmapData={filteredNighttimeData}
              deviceTracks={filteredDeviceTracks}
              temporalBreakdown={temporalBreakdown}
              activeTab={tabValue}
              selectedDwellId={selectedDwellId}
              selectedRouteId={selectedRouteId}
            />
          </AccordionDetails>
        </Accordion>

        {/* Main Content Tabs */}
        <Paper elevation={2} sx={{ borderRadius: 2, width: '100%' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="results tabs"
            variant="fullWidth"
            sx={{ 
              borderBottom: 1, 
              borderColor: 'divider',
              mb: 3,
              '& .MuiTabs-indicator': {
                height: 3
              }
            }}
          >
            <Tab 
              icon={<Assessment />} 
              iconPosition="start" 
              label="Overview"
              sx={{ 
                minHeight: 64,
                fontSize: '0.9rem',
                fontWeight: 'bold'
              }}
            />
            <Tab 
              icon={<LocationOn />} 
              iconPosition="start" 
              label="Dwell Analysis"
              sx={{ 
                minHeight: 64,
                fontSize: '0.9rem',
                fontWeight: 'bold'
              }}
            />
            <Tab 
              icon={<Route />} 
              iconPosition="start" 
              label="Dwell Route Patterns"
              sx={{ 
                minHeight: 64,
                fontSize: '0.9rem',
                fontWeight: 'bold'
              }}
            />
            <Tab 
              icon={<AccessTime />} 
              iconPosition="start" 
              label="Day vs Night Activity"
              sx={{ 
                minHeight: 64,
                fontSize: '0.9rem',
                fontWeight: 'bold'
              }}
            />
          </Tabs>

          <TabPanel value={tabValue} index={0}>
            {/* Overview Tab */}
            <Box sx={{ p: 3, width: '100%' }}>
              {/* AI Insights Section */}
                  {summaryStats?.ai_insights ? (
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      AI-Powered Insights
                      <Chip 
                        label={summaryStats.ai_insights?.type === 'ai_generated' ? 'Azure OpenAI GPT-4' : 'Rule-based Analysis'}
                        size="small"
                        color={summaryStats.ai_insights?.success ? 'success' : 'default'}
                        variant="outlined"
                      />
                    </Typography>
                    
                    {summaryStats.ai_insights?.insights && Array.isArray(summaryStats.ai_insights.insights) && summaryStats.ai_insights.insights.length > 0 ? (
                      <Box sx={{ mt: 2 }}>
                        {(() => {
                          // Parse insights - handle both array and string formats
                          let insights: string[] = [];
                          
                          // Validate insights structure
                          if (!Array.isArray(summaryStats.ai_insights.insights)) {
                            console.warn('Insights expected to be array, got:', typeof summaryStats.ai_insights.insights);
                          }
                          
                          if (Array.isArray(summaryStats.ai_insights.insights)) {
                            // Already an array - process each item
                            insights = summaryStats.ai_insights.insights.flatMap((insight: string) => {
                              if (typeof insight === 'string') {
                                // Split on bullet points and clean up
                                return insight
                                  .split(/\n+/) // Split by newlines
                                  .map((line: string) => line.trim())
                                  .filter((line: string) => line.length > 0)
                                  .map((line: string) => line.startsWith('- ') ? line.slice(2).trim() : line);
                              }
                              return [insight];
                            });
                          } else if (typeof summaryStats.ai_insights.insights === 'string') {
                            // Single string - split into bullets
                            insights = summaryStats.ai_insights.insights
                              .split(/\n+/)
                              .map((line: string) => line.trim())
                              .filter((line: string) => line.length > 0)
                              .map((line: string) => line.startsWith('- ') ? line.slice(2).trim() : line);
                          }
                          
                          // Separate summary from other insights
                          const summaryInsight = insights.find(insight => insight.toLowerCase().startsWith('summary:'));
                          const regularInsights = insights.filter(insight => !insight.toLowerCase().startsWith('summary:'));
                          
                          // Extract anomaly insights for dedicated section - matching AI's actual language
                          const anomalyInsights = regularInsights.filter((insight: string) => {
                            const lowerInsight = insight.toLowerCase();
                            return (
                              // Direct anomaly keywords
                              lowerInsight.includes('anomaly') || 
                              lowerInsight.includes('unusual') || 
                              lowerInsight.includes('deviation') || 
                              lowerInsight.includes('out-of-pattern') ||
                              lowerInsight.includes('irregular') ||
                              lowerInsight.includes('unexpected') ||
                              lowerInsight.includes('security alert') ||
                              lowerInsight.includes('surveillance') ||
                              lowerInsight.includes('outlier') ||
                              lowerInsight.includes('scattered') ||
                              // Risk and security keywords
                              lowerInsight.includes('operational security risk') ||
                              lowerInsight.includes('security risk assessment') ||
                              lowerInsight.includes('security assessment') ||
                              lowerInsight.includes('predictability risk') ||
                              lowerInsight.includes('risk assessment') ||
                              lowerInsight.includes('security considerations') ||
                              // Activity pattern keywords
                              lowerInsight.includes('high-frequency short visits') ||
                              lowerInsight.includes('frequent short visits') ||
                              lowerInsight.includes('short visits') ||
                              // Location diversity keywords
                              lowerInsight.includes('high location diversity') ||
                              lowerInsight.includes('location diversity') ||
                              lowerInsight.includes('high level of location diversity') ||
                              // Behavioral keywords  
                              lowerInsight.includes('behavioral intelligence') ||
                              lowerInsight.includes('mobility profile') ||
                              lowerInsight.includes('routine predictability') ||
                              // Security-related behavioral terms
                              lowerInsight.includes('counter-surveillance') ||
                              lowerInsight.includes('surveillance awareness') ||
                              lowerInsight.includes('operational security') ||
                              lowerInsight.includes('pattern analysis') ||
                              // Risk-related terms
                              lowerInsight.includes('high predictability') ||
                              lowerInsight.includes('medium predictability') ||
                              lowerInsight.includes('low predictability') ||
                              // AI's ACTUAL language patterns (from your screenshot)
                              lowerInsight.includes('distinct usage patterns') ||
                              lowerInsight.includes('different users or purposes') ||
                              lowerInsight.includes('low overlap between devices') ||
                              lowerInsight.includes('need for further investigation') ||
                              lowerInsight.includes('individual usage contexts') ||
                              lowerInsight.includes('suggests different usage patterns') ||
                              lowerInsight.includes('different purposes') ||
                              lowerInsight.includes('investigation into individual') ||
                              lowerInsight.includes('usage contexts') ||
                              lowerInsight.includes('highlighting the need for') ||
                              lowerInsight.includes('further investigation') ||
                              // Additional sophisticated AI patterns
                              lowerInsight.includes('distinct patterns') ||
                              lowerInsight.includes('different usage contexts') ||
                              lowerInsight.includes('suggests different users') ||
                              lowerInsight.includes('minimal concurrent usage') ||
                              lowerInsight.includes('low overlap') && lowerInsight.includes('devices')
                            );
                          });
                          
                          // Filter out anomaly insights from regular insights for other categories
                          const nonAnomalyInsights = regularInsights.filter((insight: string) => 
                            !anomalyInsights.includes(insight)
                          );
                          
                          return (
                            <>
                              {/* Dedicated Anomaly Detection Section */}
                              {anomalyInsights.length > 0 && (
                                <Paper 
                                  elevation={3} 
                                  sx={{ 
                                    p: 3, 
                                    mb: 3, 
                                    borderRadius: 2,
                                    border: '2px solid',
                                    borderColor: 'error.main',
                                    background: 'linear-gradient(135deg, #ffebee 0%, #fce4ec 100%)'
                                  }}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <WarningIcon sx={{ color: 'error.main', fontSize: 28, mr: 2 }} />
                                    <Typography 
                                      variant="h6" 
                                      sx={{ 
                                        fontWeight: 'bold', 
                                        color: 'error.main',
                                        letterSpacing: 0.5
                                      }}
                                    >
                                      Out-of-Pattern Activities & Anomaly Detection
                                    </Typography>
                                  </Box>
                                  
                                  <Box sx={{ pl: 2 }}>
                                    {anomalyInsights.map((insight: string, index: number) => (
                                      <Box key={index} sx={{ mb: 1.5, display: 'flex', alignItems: 'flex-start' }}>
                                        <SecurityIcon 
                                          sx={{ 
                                            color: 'error.main', 
                                            fontSize: 20, 
                                            mr: 1, 
                                            mt: 0.2,
                                            flexShrink: 0
                                          }} 
                                        />
                                        <Typography 
                                          variant="body2" 
                                          sx={{ 
                                            color: 'error.dark',
                                            fontWeight: 500,
                                            lineHeight: 1.4
                                          }}
                                        >
                                          {insight}
                                        </Typography>
                                      </Box>
                                    ))}
                                  </Box>
                                </Paper>
                              )}

                              {/* Grouped Analysis Sections */}
                              {nonAnomalyInsights.length > 0 && (() => {
                                // Group insights by analysis categories
                                const insightCategories = [
                                  {
                                    title: 'Device Performance Analysis',
                                    color: 'primary.main',
                                    insights: nonAnomalyInsights.filter((insight: string) => 
                                      insight.toLowerCase().includes('device') || 
                                      insight.toLowerCase().includes('vehicle') || 
                                      insight.toLowerCase().includes('phone') || 
                                      insight.toLowerCase().includes('watch') ||
                                      insight.toLowerCase().includes('comparison')
                                    )
                                  },
                                  {
                                    title: 'Location Intelligence',
                                    color: 'success.main',
                                    insights: nonAnomalyInsights.filter((insight: string) => 
                                      insight.toLowerCase().includes('location') || 
                                      insight.toLowerCase().includes('visit') || 
                                      insight.toLowerCase().includes('address') || 
                                      insight.toLowerCase().includes('destination') ||
                                      insight.toLowerCase().includes('places') ||
                                      insight.toLowerCase().includes('stops')
                                    )
                                  },
                                  {
                                    title: 'Temporal & Behavioral Patterns',
                                    color: 'warning.main',
                                    insights: nonAnomalyInsights.filter((insight: string) => 
                                      insight.toLowerCase().includes('peak') || 
                                      insight.toLowerCase().includes('activity') || 
                                      insight.toLowerCase().includes('time') || 
                                      insight.toLowerCase().includes('hour') ||
                                      insight.toLowerCase().includes('morning') ||
                                      insight.toLowerCase().includes('evening') ||
                                      insight.toLowerCase().includes('period') ||
                                      insight.toLowerCase().includes('routine')
                                    )
                                  },
                                  {
                                    title: 'Cross-Device & Route Analysis',
                                    color: 'info.main',
                                    insights: nonAnomalyInsights.filter((insight: string) => 
                                      insight.toLowerCase().includes('overlap') || 
                                      insight.toLowerCase().includes('route') || 
                                      insight.toLowerCase().includes('pattern') || 
                                      insight.toLowerCase().includes('correlation') ||
                                      insight.toLowerCase().includes('same') ||
                                      insight.toLowerCase().includes('different')
                                    )
                                  },
                                  {
                                    title: 'Geographic & Data Quality',
                                    color: 'secondary.main',
                                    insights: nonAnomalyInsights.filter((insight: string) => 
                                      insight.toLowerCase().includes('geographic') || 
                                      insight.toLowerCase().includes('spread') || 
                                      insight.toLowerCase().includes('data') || 
                                      insight.toLowerCase().includes('quality') ||
                                      insight.toLowerCase().includes('coverage') ||
                                      insight.toLowerCase().includes('metadata')
                                    )
                                  }
                                ];
                                
                                // Filter out empty categories and collect categorized insights
                                const categorizedInsights = new Set<string>();
                                const nonEmptyCategories = insightCategories.filter(cat => {
                                  if (cat.insights.length > 0) {
                                    cat.insights.forEach(insight => categorizedInsights.add(insight));
                                    return true;
                                  }
                                  return false;
                                });
                                
                                // Find uncategorized insights
                                const uncategorizedInsights = nonAnomalyInsights.filter((insight: string) => 
                                  !categorizedInsights.has(insight) && insight.length >= 10
                                );
                                    
                                    return (
                                  <Box sx={{ mb: 2 }}>
                                    {nonEmptyCategories.map((category, categoryIndex) => (
                                      <Card key={categoryIndex} sx={{ mb: 3, border: '1px solid', borderColor: 'divider' }}>
                                        <CardContent>
                                          <Typography variant="h6" sx={{ 
                                            fontWeight: 'bold', 
                                            mb: 2, 
                                            display: 'flex', 
                                            alignItems: 'center',
                                            color: category.color 
                                          }}>
                                            {category.title}
                                          </Typography>
                                          <Box sx={{ pl: 2 }}>
                                            {category.insights.map((insight: string, insightIndex: number) => (
                                              <Box key={insightIndex} sx={{ mb: 2, display: 'flex', alignItems: 'flex-start' }}>
                                                <Box
                                                  sx={{
                                                    minWidth: 24,
                                                    height: 24,
                                                    borderRadius: '50%',
                                                    backgroundColor: category.color,
                                                    color: 'white',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    mr: 2,
                                                    mt: 0.2,
                                                    fontSize: 11,
                                                    fontWeight: 'bold'
                                                  }}
                                                >
                                                  {insightIndex + 1}
                                                </Box>
                                                <Typography variant="body1" sx={{ lineHeight: 1.6, flex: 1 }}>
                                            {insight}
                                          </Typography>
                                              </Box>
                                            ))}
                                          </Box>
                                        </CardContent>
                                      </Card>
                                    ))}
                                    
                                    {/* Handle any uncategorized insights */}
                                    {uncategorizedInsights.length > 0 && (
                                      <Card sx={{ mb: 3, border: '1px solid', borderColor: 'divider' }}>
                                        <CardContent>
                                          <Typography variant="h6" sx={{ 
                                            fontWeight: 'bold', 
                                            mb: 2, 
                                            display: 'flex', 
                                            alignItems: 'center',
                                            color: 'text.primary'
                                          }}>
                                            Additional Insights
                                          </Typography>
                                          <Box sx={{ pl: 2 }}>
                                            {uncategorizedInsights.map((insight: string, insightIndex: number) => (
                                              <Box key={insightIndex} sx={{ mb: 2, display: 'flex', alignItems: 'flex-start' }}>
                                                <Box
                                                  sx={{
                                                    minWidth: 24,
                                                    height: 24,
                                                    borderRadius: '50%',
                                                    backgroundColor: 'grey.500',
                                                    color: 'white',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    mr: 2,
                                                    mt: 0.2,
                                                    fontSize: 11,
                                                    fontWeight: 'bold'
                                                  }}
                                                >
                                                  {insightIndex + 1}
                                                </Box>
                                                <Typography variant="body1" sx={{ lineHeight: 1.6, flex: 1 }}>
                                                  {insight}
                                                </Typography>
                                              </Box>
                                            ))}
                                          </Box>
                                        </CardContent>
                                      </Card>
                                    )}
                                  </Box>
                                );
                              })()}
                              
                              {/* Summary insight as full-width card */}
                              {summaryInsight && (
                                <Alert severity="success" sx={{ mt: 2 }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                    Executive Summary
                                  </Typography>
                                  <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                                    {summaryInsight.startsWith('Summary: ') 
                                      ? summaryInsight.slice(9).trim() 
                                      : summaryInsight
                                    }
                                  </Typography>
                                </Alert>
                              )}
                            </>
                          );
                        })()}
                        
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', fontStyle: 'italic' }}>
                          {summaryStats.ai_insights.success 
                            ? 'Generated using AI analysis of movement patterns, dwell behaviors, and temporal data'
                            : 'Generated using deterministic analysis of tracking data'
                          }
                        </Typography>
                      </Box>
                    ) : (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        <Typography variant="body2">
                          AI insights are not available for this analysis. 
                          {!summaryStats.ai_insights.success && (
                            <span>
                              <br/>To enable AI insights: Configure Azure OpenAI credentials and enable AI insights toggle during analysis.
                            </span>
                          )}
                        </Typography>
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <Alert severity="info">
                  <Typography variant="body1">
                    <strong>AI Insights Not Available</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    AI insights were not generated for this analysis. This typically means the "AI Insights" toggle was disabled during analysis setup.
                  </Typography>
                  
                  <Typography variant="body2" sx={{ mt: 2, fontWeight: 'bold' }}>
                    To Enable AI Insights:
                  </Typography>
                  <Box component="ol" sx={{ mt: 1, pl: 2 }}>
                    <li>Ensure the "AI Insights" toggle is <strong>enabled</strong> when starting a new analysis</li>
                    <li>Verify Azure OpenAI credentials are configured in the backend</li>
                    <li>Check that the Azure OpenAI service is accessible</li>
                  </Box>
                  
                  <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic', color: 'text.secondary' }}>
                    AI insights use Azure OpenAI GPT-4 to analyze movement patterns, dwell behaviors, and temporal data 
                    to provide intelligent observations about the analysis results.
                  </Typography>
                </Alert>
              )}
            </Box>
          </TabPanel>


          <TabPanel value={tabValue} index={1}>
            {/* Dwell Locations Analysis */}
            <Box sx={{ p: 3, width: '100%' }}>
              {/* Search and Filter Controls */}
              <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField
                  size="small"
                  placeholder="Search locations by name, address, type, device..."
                  value={dwellSearchTerm}
                  onChange={(e) => {
                    setDwellSearchTerm(e.target.value);
                    setDwellPage(0); // Reset to first page when searching
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                    endAdornment: dwellSearchTerm && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setDwellSearchTerm('')}
                          edge="end"
                        >
                          <Clear />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ minWidth: 300 }}
                />
                {dwellSearchTerm && (
                  <Chip 
                    label={`${dwellData.total} locations found`}
                    size="small"
                    color="info"
                    onDelete={() => setDwellSearchTerm('')}
                  />
                )}
              </Box>

              {/* Table-Map Interaction Help */}
              <Alert severity="info" sx={{ mb: 2, fontSize: '0.85rem' }} icon={<LightbulbIcon />}>
                <strong>Click any table row</strong> to focus the map on that specific location or route. Click again to show all data.
                <br />
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}><BarChartIcon sx={{ fontSize: 14 }} /> <strong>Click column headers</strong> to sort data ascending/descending (visits, duration, name, etc.)</Box>
              </Alert>
              
              {/* Temporal Visualization Toggle */}
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="h6" component="span">
                  Temporal Analysis:
                </Typography>
                <ToggleButtonGroup
                  value={temporalVisualization}
                  exclusive
                  onChange={(_, newValue) => {
                    if (newValue !== null) {
                      setTemporalVisualization(newValue);
                    }
                  }}
                  size="small"
                >
                  <ToggleButton value="heatmap">
                    <GridView sx={{ mr: 1 }} />
                    Heatmap
                  </ToggleButton>
                  <ToggleButton value="timeline">
                    <Timeline sx={{ mr: 1 }} />
                    Timeline
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {/* Conditional Temporal Visualization */}
              {temporalVisualization === 'heatmap' ? (
                <DwellHeatmapVisx 
                  dwellLocations={combinedDwellLocations} 
                  temporalBreakdown={temporalBreakdown}
                  deviceBreakdown={summaryStats.device_breakdown}
                  deviceResults={summaryStats.device_results}
                  isMultiDevice={isMultiDevice}
                  selectedDeviceFilter={currentDeviceFilter}
                />
              ) : (
                <DwellGanttChart
                  dwellLocations={combinedDwellLocations}
                  temporalBreakdown={temporalBreakdown}
                  deviceBreakdown={summaryStats.device_breakdown}
                  deviceResults={summaryStats.device_results}
                  isMultiDevice={isMultiDevice}
                  selectedDeviceFilter={currentDeviceFilter}
                />
              )}
              
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <TableSortLabel
                          active={dwellSortBy === 'place_name'}
                          direction={dwellSortBy === 'place_name' ? dwellSortOrder : 'asc'}
                          onClick={() => handleDwellSort('place_name')}
                        >
                          <strong>Place Name</strong>
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={dwellSortBy === 'place_type'}
                          direction={dwellSortBy === 'place_type' ? dwellSortOrder : 'asc'}
                          onClick={() => handleDwellSort('place_type')}
                        >
                          <strong>Category</strong>
                        </TableSortLabel>
                      </TableCell>
                      <TableCell><strong>Address & Coordinates</strong></TableCell>
                      {isMultiDevice && (
                        <TableCell>
                          <TableSortLabel
                            active={dwellSortBy === 'device_type'}
                            direction={dwellSortBy === 'device_type' ? dwellSortOrder : 'asc'}
                            onClick={() => handleDwellSort('device_type')}
                          >
                            <strong>Device Type</strong>
                          </TableSortLabel>
                        </TableCell>
                      )}
                      <TableCell align="right">
                        <TableSortLabel
                          active={dwellSortBy === 'total_visits'}
                          direction={dwellSortBy === 'total_visits' ? dwellSortOrder : 'asc'}
                          onClick={() => handleDwellSort('total_visits')}
                        >
                          <strong>Visits</strong>
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right">
                        <TableSortLabel
                          active={dwellSortBy === 'total_duration'}
                          direction={dwellSortBy === 'total_duration' ? dwellSortOrder : 'asc'}
                          onClick={() => handleDwellSort('total_duration')}
                        >
                          <strong>Total Duration</strong>
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right">
                        <TableSortLabel
                          active={dwellSortBy === 'average_duration'}
                          direction={dwellSortBy === 'average_duration' ? dwellSortOrder : 'asc'}
                          onClick={() => handleDwellSort('average_duration')}
                        >
                          <strong>Avg Duration</strong>
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right"><strong>Max Duration</strong></TableCell>
                      <TableCell><strong>Entities</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dwellData.loading ? (
                      <TableRow>
                        <TableCell colSpan={isMultiDevice ? 7 : 6} align="center" sx={{ py: 4 }}>
                          <CircularProgress size={24} />
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Loading dwell locations...
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : combinedDwellLocations.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={isMultiDevice ? 7 : 6} align="center" sx={{ py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            No dwell locations found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : combinedDwellLocations.map((dwell, index) => (
                      <TableRow 
                        key={index} 
                        hover
                        selected={selectedDwellId === dwell.id}
                        onClick={() => {
                          if (selectedDwellId === dwell.id) {
                            setSelectedDwellId(null); // Deselect if already selected
                          } else {
                            setSelectedDwellId(dwell.id);
                            setSelectedRouteId(null); // Clear route selection
                          }
                        }}
                        sx={{ 
                          cursor: 'pointer',
                          bgcolor: selectedDwellId === dwell.id ? 'action.selected' : 'transparent',
                          '&:hover': {
                            bgcolor: selectedDwellId === dwell.id ? 'action.selected' : 'action.hover'
                          }
                        }}
                      >
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            <Badge badgeContent={dwell.total_visits || 0} color="primary" max={999}>
                              <Place color="action" sx={{ mr: 1 }} />
                            </Badge>
                            <Box sx={{ ml: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {dwell.place_name || `Location ${dwellPage * 10 + index + 1}`}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {dwell.place_type && dwell.place_type !== 'dwell_location' ? (
                            <Chip 
                              label={dwell.place_type.replace(/[_-]/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} 
                              size="small" 
                              variant="filled" 
                              color={
                                dwell.place_type.toLowerCase().includes('restaurant') ? 'error' :
                                dwell.place_type.toLowerCase().includes('gym') || dwell.place_type.toLowerCase().includes('health') ? 'success' :
                                dwell.place_type.toLowerCase().includes('university') || dwell.place_type.toLowerCase().includes('school') ? 'info' :
                                'secondary'
                              }
                              sx={{ fontWeight: 500 }}
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                              Category unknown
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {dwell.address && dwell.address !== 'N/A' && dwell.address !== 'Address not available'
                                ? dwell.address
                                : 'Address not available'
                              }
                            </Typography>
                            {dwell.confidence_score && dwell.confidence_score < 1.0 && (
                              <Typography variant="caption" color="text.secondary">
                                Confidence: {Math.round(dwell.confidence_score * 100)}%
                              </Typography>
                            )}
                            {/* Show coordinates as fallback if no real address */}
                            {(!dwell.address || dwell.address === 'N/A' || dwell.address === 'Address not available') && (
                              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                {dwell.center_latitude?.toFixed(4)}, {dwell.center_longitude?.toFixed(4)}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        {isMultiDevice && (
                          <TableCell>
                            <Chip 
                              label={dwell.device_type || 'Unknown'} 
                              size="small" 
                              color={
                                dwell.device_type === 'Vehicle' ? 'secondary' :
                                dwell.device_type === 'Phone' ? 'primary' :
                                dwell.device_type === 'Watch' ? 'success' : 'default'
                              }
                              variant="filled"
                            />
                          </TableCell>
                        )}
                        <TableCell align="right">
                          <Chip 
                            label={dwell.total_visits || 0} 
                            size="small" 
                            color="primary" 
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {formatDuration(dwell.total_duration || 0)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {formatDuration(dwell.average_duration || 0)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {formatDuration(dwell.max_duration || 0)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {dwell.students?.slice(0, 3).map((student, idx) => (
                              <Chip key={`${student}-${idx}`} label={student} size="small" variant="outlined" />
                            ))}
                            {(dwell.students?.length || 0) > 3 && (
                              <Chip 
                                label={`+${(dwell.students?.length || 0) - 3} more`} 
                                size="small" 
                                color="secondary"
                              />
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Showing {dwellPage * dwellRowsPerPage + 1}-{Math.min((dwellPage + 1) * dwellRowsPerPage, dwellData.total)} of {dwellData.total} locations
                </Typography>
                <TablePagination
                  component="div"
                  count={dwellData.total}
                  page={dwellPage}
                  onPageChange={(_e, newPage) => setDwellPage(newPage)}
                  rowsPerPage={dwellRowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setDwellRowsPerPage(parseInt(e.target.value, 10));
                    setDwellPage(0);
                  }}
                  rowsPerPageOptions={[10, 20, 50, 100]}
                  labelRowsPerPage="Per page:"
                  showFirstButton
                  showLastButton
                  sx={{ 
                    '& .MuiTablePagination-toolbar': { 
                      minHeight: '52px',
                      paddingLeft: 2,
                      paddingRight: 2 
                    },
                    '& .MuiTablePagination-selectLabel': { 
                      margin: 0 
                    },
                    '& .MuiTablePagination-displayedRows': { 
                      margin: 0 
                    }
                  }}
                />
              </Box>
            </Box>
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            {/* Dwell Route Patterns */}
            <Box sx={{ p: 3, width: '100%' }}>
              {/* Search and Filter Controls */}
              <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField
                  size="small"
                  placeholder="Search routes by name, places, device type, distance..."
                  value={routeSearchTerm}
                  onChange={(e) => {
                    setRouteSearchTerm(e.target.value);
                    setRoutePage(0); // Reset to first page when searching
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                    endAdornment: routeSearchTerm && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setRouteSearchTerm('')}
                          edge="end"
                        >
                          <Clear />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ minWidth: 300 }}
                />
                {routeSearchTerm && (
                  <Chip 
                    label={`${routeData.total} routes found`}
                    size="small"
                    color="info"
                    onDelete={() => setRouteSearchTerm('')}
                  />
                )}
              </Box>

              {/* Table-Map Interaction Help */}
              <Alert severity="info" sx={{ mb: 2, fontSize: '0.85rem' }} icon={<LightbulbIcon />}>
                <strong>Click any table row</strong> to focus the map on that specific location or route. Click again to show all data.
                <br />
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}><BarChartIcon sx={{ fontSize: 14 }} /> <strong>Click column headers</strong> to sort data ascending/descending (frequency, distance, duration, etc.)</Box>
              </Alert>
              
              {/* Route Temporal Visualization Toggle */}
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="h6" component="span">
                  Route Temporal Analysis:
                </Typography>
                <ToggleButtonGroup
                  value={routeTemporalVisualization}
                  exclusive
                  onChange={(_, newValue) => {
                    if (newValue !== null) {
                      setRouteTemporalVisualization(newValue);
                    }
                  }}
                  size="small"
                >
                  <ToggleButton value="heatmap">
                    <GridView sx={{ mr: 1 }} />
                    Heatmap
                  </ToggleButton>
                  <ToggleButton value="timeline">
                    <Timeline sx={{ mr: 1 }} />
                    Timeline
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {/* Conditional Route Temporal Visualization */}
              {routeTemporalVisualization === 'heatmap' ? (
                <RouteHeatmapVisx 
                  routes={combinedRoutes} 
                  temporalBreakdown={temporalBreakdown}
                  deviceBreakdown={summaryStats.device_breakdown}
                  deviceResults={summaryStats.device_results}
                  isMultiDevice={isMultiDevice}
                  selectedDeviceFilter={currentDeviceFilter}
                />
              ) : (
                <RouteGanttChart
                  routes={combinedRoutes}
                  temporalBreakdown={temporalBreakdown}
                  deviceBreakdown={summaryStats.device_breakdown}
                  deviceResults={summaryStats.device_results}
                  isMultiDevice={isMultiDevice}
                  selectedDeviceFilter={currentDeviceFilter}
                />
              )}
              
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <TableSortLabel
                          active={routeSortBy === 'route_name'}
                          direction={routeSortBy === 'route_name' ? routeSortOrder : 'asc'}
                          onClick={() => handleRouteSort('route_name')}
                        >
                          <strong>Route</strong>
                        </TableSortLabel>
                      </TableCell>
                      {isMultiDevice && (
                        <TableCell>
                          <TableSortLabel
                            active={routeSortBy === 'device_type'}
                            direction={routeSortBy === 'device_type' ? routeSortOrder : 'asc'}
                            onClick={() => handleRouteSort('device_type')}
                          >
                            <strong>Device Type</strong>
                          </TableSortLabel>
                        </TableCell>
                      )}
                      <TableCell align="right">
                        <TableSortLabel
                          active={routeSortBy === 'frequency'}
                          direction={routeSortBy === 'frequency' ? routeSortOrder : 'asc'}
                          onClick={() => handleRouteSort('frequency')}
                        >
                          <strong>Frequency</strong>
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right">
                        <TableSortLabel
                          active={routeSortBy === 'distance'}
                          direction={routeSortBy === 'distance' ? routeSortOrder : 'asc'}
                          onClick={() => handleRouteSort('distance')}
                        >
                          <strong>Distance</strong>
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right">
                        <TableSortLabel
                          active={routeSortBy === 'average_duration'}
                          direction={routeSortBy === 'average_duration' ? routeSortOrder : 'asc'}
                          onClick={() => handleRouteSort('average_duration')}
                        >
                          <strong>Avg Duration</strong>
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={routeSortBy === 'students'}
                          direction={routeSortBy === 'students' ? routeSortOrder : 'asc'}
                          onClick={() => handleRouteSort('students')}
                        >
                          <strong>{singular}</strong>
                        </TableSortLabel>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {routeData.loading ? (
                      <TableRow>
                        <TableCell colSpan={isMultiDevice ? 6 : 5} align="center" sx={{ py: 4 }}>
                          <CircularProgress size={24} />
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Loading routes...
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : combinedRoutes.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={isMultiDevice ? 6 : 5} align="center" sx={{ py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            No routes found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : combinedRoutes.map((route, index) => (
                      <TableRow 
                        key={index} 
                        hover
                        selected={selectedRouteId === route.id}
                        onClick={() => {
                          if (selectedRouteId === route.id) {
                            setSelectedRouteId(null); // Deselect if already selected
                          } else {
                            setSelectedRouteId(route.id);
                            setSelectedDwellId(null); // Clear dwell selection
                          }
                        }}
                        sx={{ 
                          cursor: 'pointer',
                          bgcolor: selectedRouteId === route.id ? 'action.selected' : 'transparent',
                          '&:hover': {
                            bgcolor: selectedRouteId === route.id ? 'action.selected' : 'action.hover'
                          }
                        }}
                      >
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            <Box 
                              sx={{ 
                                width: 16, 
                                height: 4, 
                                backgroundColor: `hsl(${(index * 137.5) % 360}, 70%, 45%)`,
                                mr: 1,
                                borderRadius: 1
                              }} 
                            />
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {route.route_name || `Route ${routePage * 10 + index + 1}`}
                            </Typography>
                          </Box>
                        </TableCell>
                        {isMultiDevice && (
                          <TableCell>
                            <Chip 
                              label={route.device_type || 'Unknown'} 
                              size="small" 
                              color={
                                route.device_type === 'Vehicle' ? 'secondary' :
                                route.device_type === 'Phone' ? 'primary' :
                                route.device_type === 'Watch' ? 'success' : 'default'
                              }
                              variant="filled"
                            />
                          </TableCell>
                        )}
                        <TableCell align="right">
                          <Chip 
                            label={route.frequency || 0} 
                            size="small" 
                            color="secondary" 
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {formatDistance(route.distance || 0)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {formatDuration(route.average_duration || 0)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {route.students?.slice(0, 2).map((student: string, idx: number) => (
                              <Chip key={`${student}-${idx}`} label={student} size="small" variant="outlined" />
                            ))}
                            {(route.students?.length || 0) > 2 && (
                              <Chip 
                                label={`+${(route.students?.length || 0) - 2} more`} 
                                size="small" 
                                color="secondary"
                              />
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Showing {routePage * routeRowsPerPage + 1}-{Math.min((routePage + 1) * routeRowsPerPage, routeData.total)} of {routeData.total} routes
                </Typography>
                <TablePagination
                  component="div"
                  count={routeData.total}
                  page={routePage}
                  onPageChange={(_e, newPage) => setRoutePage(newPage)}
                  rowsPerPage={routeRowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setRouteRowsPerPage(parseInt(e.target.value, 10));
                    setRoutePage(0);
                  }}
                  rowsPerPageOptions={[10, 20, 50, 100]}
                  labelRowsPerPage="Per page:"
                  showFirstButton
                  showLastButton
                  sx={{ 
                    '& .MuiTablePagination-toolbar': { 
                      minHeight: '52px',
                      paddingLeft: 2,
                      paddingRight: 2 
                    },
                    '& .MuiTablePagination-selectLabel': { 
                      margin: 0 
                    },
                    '& .MuiTablePagination-displayedRows': { 
                      margin: 0 
                    }
                  }}
                />
              </Box>
            </Box>
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            {/* Day vs Night Activity */}
            <Box sx={{ p: 3, width: '100%' }}>
              <Grid container spacing={3}>
                {/* Summary Cards */}
                <Grid item xs={12}>
                  <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AccessTime color="primary" />
                    Temporal Activity Analysis
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Compare activity patterns between daytime (6 AM - 6 PM) and nighttime (6 PM - 6 AM) periods.
                  </Typography>
                </Grid>

                {/* Day vs Night Summary */}
                {summaryStats?.temporal_breakdown && typeof summaryStats.temporal_breakdown === 'object' && (
                  <>
                    <Grid item xs={12} md={4}>
                      <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)', color: 'white' }}>
                        <CardContent sx={{ textAlign: 'center', py: 3 }}>
                          <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                            {summaryStats.temporal_breakdown.daytime_points?.toLocaleString() || '0'}
                          </Typography>
                          <Typography variant="h6" sx={{ opacity: 0.9, mb: 1 }}>
                            Daytime Points
                          </Typography>
                          <Typography variant="caption" display="block" sx={{ opacity: 0.8 }}>
                            6 AM - 6 PM Activity
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #4F46E5 0%, #3730A3 100%)', color: 'white' }}>
                        <CardContent sx={{ textAlign: 'center', py: 3 }}>
                          <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                            {summaryStats.temporal_breakdown.nighttime_points?.toLocaleString() || '0'}
                          </Typography>
                          <Typography variant="h6" sx={{ opacity: 0.9, mb: 1 }}>
                            Nighttime Points
                          </Typography>
                          <Typography variant="caption" display="block" sx={{ opacity: 0.8 }}>
                            6 PM - 6 AM Activity
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #14B8A6 0%, #0D9488 100%)', color: 'white' }}>
                        <CardContent sx={{ textAlign: 'center', py: 3 }}>
                          <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                            {summaryStats.temporal_breakdown.total_points?.toLocaleString() || '0'}
                          </Typography>
                          <Typography variant="h6" sx={{ opacity: 0.9, mb: 1 }}>
                            Total Points
                          </Typography>
                          <Typography variant="caption" display="block" sx={{ opacity: 0.8 }}>
                            24-Hour Activity
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Activity Ratio */}
                    <Grid item xs={12}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            Day vs Night Activity Distribution
                          </Typography>
                          {(() => {
                            const daytime = summaryStats.temporal_breakdown?.daytime_points || 0;
                            const nighttime = summaryStats.temporal_breakdown?.nighttime_points || 0;
                            const total = daytime + nighttime;
                            const dayPercentage = total > 0 ? ((daytime / total) * 100).toFixed(1) : '0';
                            const nightPercentage = total > 0 ? ((nighttime / total) * 100).toFixed(1) : '0';
                            
                            return (
                              <Grid container spacing={3} sx={{ mt: 1 }}>
                                <Grid item xs={12} md={6}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#3B82F6' }}>
                                      Daytime Activity: {dayPercentage}%
                                    </Typography>
                                  </Box>
                                  <LinearProgress 
                                    variant="determinate" 
                                    value={parseFloat(dayPercentage)} 
                                    sx={{ 
                                      height: 10, 
                                      borderRadius: 5,
                                      '& .MuiLinearProgress-bar': {
                                        backgroundColor: '#3B82F6'
                                      }
                                    }}
                                  />
                                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                    Higher daytime activity suggests typical work/school patterns
                                  </Typography>
                                </Grid>
                                
                                <Grid item xs={12} md={6}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#4F46E5' }}>
                                      Nighttime Activity: {nightPercentage}%
                                    </Typography>
                                  </Box>
                                  <LinearProgress 
                                    variant="determinate" 
                                    value={parseFloat(nightPercentage)} 
                                    sx={{ 
                                      height: 10, 
                                      borderRadius: 5,
                                      '& .MuiLinearProgress-bar': {
                                        backgroundColor: '#4F46E5'
                                      }
                                    }}
                                  />
                                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                    Higher nighttime activity may indicate evening/night shift patterns
                                  </Typography>
                                </Grid>
                              </Grid>
                            );
                          })()}
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Activity Insights */}
                    <Grid item xs={12}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            Temporal Pattern Insights
                          </Typography>
                          <Grid container spacing={2}>
                            {(() => {
                              const daytime = summaryStats.temporal_breakdown?.daytime_points || 0;
                              const nighttime = summaryStats.temporal_breakdown?.nighttime_points || 0;
                              const total = daytime + nighttime;
                              const dayRatio = total > 0 ? daytime / total : 0;
                              
                              const insights: Array<{
                                type: 'success' | 'info' | 'warning';
                                title: string;
                                description: string;
                              }> = [];
                              
                              if (dayRatio > 0.7) {
                                insights.push({
                                  type: 'success' as const,
                                  title: 'Primary Daytime Activity',
                                  description: 'Strong daytime activity pattern suggests typical work, school, or business hours usage.'
                                });
                              } else if (dayRatio < 0.3) {
                                insights.push({
                                  type: 'info' as const,
                                  title: 'Primary Nighttime Activity', 
                                  description: 'High nighttime activity may indicate night shift work, evening activities, or security patrols.'
                                });
                              } else {
                                insights.push({
                                  type: 'warning' as const,
                                  title: 'Balanced Activity Pattern',
                                  description: 'Activity is distributed across both day and night periods, suggesting 24/7 operations or mixed schedules.'
                                });
                              }
                              
                              if (total > 10000) {
                                insights.push({
                                  type: 'info' as const,
                                  title: 'High Activity Volume',
                                  description: `${total.toLocaleString()} total GPS points indicate extensive tracking coverage and detailed movement data.`
                                });
                              }
                              
                              return insights.map((insight, index) => (
                                <Grid item xs={12} md={6} key={index}>
                                  <Alert severity={insight.type} sx={{ height: '100%' }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                      {insight.title}
                                    </Typography>
                                    <Typography variant="body2">
                                      {insight.description}
                                    </Typography>
                                  </Alert>
                                </Grid>
                              ));
                            })()}
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>
                  </>
                )}
                
                {!summaryStats?.temporal_breakdown && (
                  <Grid item xs={12}>
                    <Alert severity="info">
                      <Typography variant="body1">
                        Temporal breakdown data is not available for this analysis. 
                        This feature requires GPS tracking data with timestamps.
                      </Typography>
                    </Alert>
                  </Grid>
                )}
              </Grid>
            </Box>
          </TabPanel>

          {/* TabPanel for Connection Analysis removed. Will be moved to its own page. */}
        </Paper>
      </div>

    </Box>
  );
};

export default EnhancedResultsViewer;
